import { UINT32_RANGE } from "./rng";

/**
 * Seeded 2D value noise for procgen (Increment 3.18). A pure function of position and salt:
 * integer lattice points are hashed, the four corners around a sample are blended with a
 * smoothstep, so the same inputs give the same value on every machine and the field stays
 * inside the determinism contract (no state, no clock, no Math.random). Value noise with two
 * octaves is enough at map scale (a 91-hex disc); simplex would buy isotropy nobody can see
 * here at three times the code.
 */
const LATTICE_MIX_X = 0x27d4eb2d;
const LATTICE_MIX_Y = 0x165667b1;
const AVALANCHE_A = 0x2c1b3c6d;
const AVALANCHE_B = 0x297a2d39;
const OCTAVE_SALT_STEP = 0x9e3779b9;

/** Uniform float in [0, 1) for one lattice point; a small integer hash with two avalanche rounds. */
function latticeValue(x: number, y: number, salt: number): number {
  let hash = (salt ^ Math.imul(x, LATTICE_MIX_X) ^ Math.imul(y, LATTICE_MIX_Y)) >>> 0;
  hash = Math.imul(hash ^ (hash >>> 15), AVALANCHE_A);
  hash = Math.imul(hash ^ (hash >>> 12), AVALANCHE_B);
  return ((hash ^ (hash >>> 15)) >>> 0) / UINT32_RANGE;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Value noise in [0, 1]: the four lattice corners around (x, y) blended with a smoothstep. */
export function valueNoise(x: number, y: number, salt: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const tx = smoothstep(x - x0);
  const ty = smoothstep(y - y0);
  const topLeft = latticeValue(x0, y0, salt);
  const topRight = latticeValue(x0 + 1, y0, salt);
  const bottomLeft = latticeValue(x0, y0 + 1, salt);
  const bottomRight = latticeValue(x0 + 1, y0 + 1, salt);
  const top = topLeft + (topRight - topLeft) * tx;
  const bottom = bottomLeft + (bottomRight - bottomLeft) * tx;
  return top + (bottom - top) * ty;
}

/**
 * Layered value noise in [0, 1]: each octave doubles the frequency and halves the weight,
 * with its own salt so the layers are independent. Two octaves give the map ridges with a
 * little roughness on them; more would be lost below one hex.
 */
export function fractalNoise(x: number, y: number, salt: number, octaves: number): number {
  let sum = 0;
  let totalWeight = 0;
  let weight = 1;
  let frequency = 1;
  for (let octave = 0; octave < octaves; octave += 1) {
    const octaveSalt = (salt + octave * OCTAVE_SALT_STEP) >>> 0;
    sum += weight * valueNoise(x * frequency, y * frequency, octaveSalt);
    totalWeight += weight;
    weight /= 2;
    frequency *= 2;
  }
  return sum / totalWeight;
}
